import { XMLBuilder, XMLParser } from 'fast-xml-parser';
import { MarkerType, type Viewport } from '@xyflow/react';
import type { AppEdge, AppNode, EdgePathType, NodeKind } from '../types';
import { EDGE_PATH_TYPES, NODE_KINDS } from '../types';
import { newId, sortParentsFirst } from '../store/flowStore';

export type FlowDocument = {
  nodes: AppNode[];
  edges: AppEdge[];
  viewport: Viewport | null;
  name: string;
};

const builder = new XMLBuilder({
  ignoreAttributes: false,
  attributeNamePrefix: '@_',
  format: true,
  indentBy: '  ',
  suppressEmptyNode: true,
  suppressBooleanAttributes: false,
});

const parser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: '@_',
  isArray: (name) => name === 'node' || name === 'edge',
  parseAttributeValue: false,
  parseTagValue: false,
  trimValues: false,
  allowBooleanAttributes: true,
});

export function exportToXml(doc: FlowDocument): string {
  const xmlObject = {
    '?xml': { '@_version': '1.0', '@_encoding': 'UTF-8' },
    flowchart: {
      '@_version': '1',
      '@_name': doc.name,
      ...(doc.viewport
        ? {
            viewport: {
              '@_x': String(doc.viewport.x),
              '@_y': String(doc.viewport.y),
              '@_zoom': String(doc.viewport.zoom),
            },
          }
        : {}),
      nodes: {
        node: doc.nodes.map((n) => ({
          '@_id': n.id,
          '@_type': n.type ?? 'process',
          '@_x': String(n.position.x),
          '@_y': String(n.position.y),
          '@_width': String(n.measured?.width ?? n.width ?? 180),
          '@_height': String(n.measured?.height ?? n.height ?? 70),
          ...(n.parentId ? { '@_parent': n.parentId } : {}),
          ...(n.data.color ? { '@_color': n.data.color } : {}),
          label: n.data.label ?? '',
        })),
      },
      edges: {
        edge: doc.edges.map((e) => ({
          '@_id': e.id,
          '@_source': e.source,
          '@_target': e.target,
          ...(e.sourceHandle ? { '@_sourceHandle': e.sourceHandle } : {}),
          ...(e.targetHandle ? { '@_targetHandle': e.targetHandle } : {}),
          '@_type': e.data?.pathType ?? 'smoothstep',
          '@_animated': e.animated ? 'true' : 'false',
          ...(typeof e.style?.stroke === 'string'
            ? { '@_color': e.style.stroke }
            : {}),
          label: e.data?.label ?? '',
        })),
      },
    },
  };
  return builder.build(xmlObject) as string;
}

type RawAttrs = Record<string, unknown>;

function attr(obj: RawAttrs, name: string): string | undefined {
  const value = obj[`@_${name}`];
  return value === undefined ? undefined : String(value);
}

function numAttr(obj: RawAttrs, name: string): number | undefined {
  const raw = attr(obj, name);
  if (raw === undefined) return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
}

function textOf(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'object') {
    const inner = (value as Record<string, unknown>)['#text'];
    return inner === undefined ? '' : String(inner);
  }
  return String(value);
}

/** Faz o parse e a validação de um XML no formato próprio. Lança Error com mensagem amigável. */
export function importFromXml(xml: string): FlowDocument {
  let parsed: Record<string, unknown>;
  try {
    parsed = parser.parse(xml) as Record<string, unknown>;
  } catch {
    throw new Error('O arquivo não contém um XML válido.');
  }

  const flowchart = parsed.flowchart as Record<string, unknown> | undefined;
  if (!flowchart) {
    throw new Error('XML inválido: elemento raiz <flowchart> não encontrado.');
  }

  const name = attr(flowchart, 'name') ?? 'Fluxograma importado';

  let viewport: Viewport | null = null;
  const rawViewport = flowchart.viewport as RawAttrs | undefined;
  if (rawViewport) {
    const x = numAttr(rawViewport, 'x');
    const y = numAttr(rawViewport, 'y');
    const zoom = numAttr(rawViewport, 'zoom');
    if (x !== undefined && y !== undefined && zoom !== undefined && zoom > 0) {
      viewport = { x, y, zoom };
    }
  }

  const rawNodes =
    ((flowchart.nodes as Record<string, unknown> | undefined)?.node as
      | RawAttrs[]
      | undefined) ?? [];
  const rawEdges =
    ((flowchart.edges as Record<string, unknown> | undefined)?.edge as
      | RawAttrs[]
      | undefined) ?? [];

  const nodes: AppNode[] = [];
  const nodeIds = new Set<string>();

  for (const [index, raw] of rawNodes.entries()) {
    const id = attr(raw, 'id') ?? newId();
    if (nodeIds.has(id)) {
      throw new Error(`XML inválido: id de nó duplicado ("${id}").`);
    }
    const type = attr(raw, 'type') ?? 'process';
    if (!NODE_KINDS.includes(type as NodeKind)) {
      throw new Error(
        `XML inválido: tipo de nó desconhecido ("${type}") no nó ${index + 1}.`,
      );
    }
    const x = numAttr(raw, 'x');
    const y = numAttr(raw, 'y');
    if (x === undefined || y === undefined) {
      throw new Error(
        `XML inválido: posição ausente ou inválida no nó "${id}".`,
      );
    }
    nodeIds.add(id);
    nodes.push({
      id,
      type,
      position: { x, y },
      width: numAttr(raw, 'width') ?? 180,
      height: numAttr(raw, 'height') ?? 70,
      parentId: attr(raw, 'parent') || undefined,
      data: {
        label: textOf(raw.label),
        color: attr(raw, 'color'),
      },
    });
  }

  for (const node of nodes) {
    if (node.parentId && !nodeIds.has(node.parentId)) {
      throw new Error(
        `XML inválido: o nó "${node.id}" referencia um pai inexistente ("${node.parentId}").`,
      );
    }
  }

  const edges: AppEdge[] = [];
  const edgeIds = new Set<string>();

  for (const [index, raw] of rawEdges.entries()) {
    const id = attr(raw, 'id') ?? newId();
    if (edgeIds.has(id)) {
      throw new Error(`XML inválido: id de conexão duplicado ("${id}").`);
    }
    const source = attr(raw, 'source');
    const target = attr(raw, 'target');
    if (!source || !target) {
      throw new Error(
        `XML inválido: conexão ${index + 1} sem origem ou destino.`,
      );
    }
    if (!nodeIds.has(source) || !nodeIds.has(target)) {
      throw new Error(
        `XML inválido: a conexão "${id}" referencia nós inexistentes.`,
      );
    }
    const rawType = attr(raw, 'type') ?? 'smoothstep';
    const pathType: EdgePathType = EDGE_PATH_TYPES.includes(
      rawType as EdgePathType,
    )
      ? (rawType as EdgePathType)
      : 'smoothstep';
    const color = attr(raw, 'color');
    edgeIds.add(id);
    edges.push({
      id,
      source,
      target,
      sourceHandle: attr(raw, 'sourceHandle'),
      targetHandle: attr(raw, 'targetHandle'),
      type: 'labeled',
      animated: attr(raw, 'animated') === 'true',
      markerEnd: { type: MarkerType.ArrowClosed, ...(color ? { color } : {}) },
      ...(color ? { style: { stroke: color } } : {}),
      data: { label: textOf(raw.label), pathType },
    });
  }

  return { nodes: sortParentsFirst(nodes), edges, viewport, name };
}
